import React from 'react'
import { Link } from 'react-router-dom'
import type { Period, Summary } from './statsAggregations.js'

export const SummaryLine: React.FC<{ summary: Summary }> = ({ summary }) => {
  const round = (n: number): string => (Number.isInteger(n) ? String(n) : n.toFixed(1))
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 mb-2">
      <span>n {summary.count}</span>
      <span>min {round(summary.min)}</span>
      <span>p20 {round(summary.p20)}</span>
      <span>median {round(summary.median)}</span>
      <span>avg {round(summary.avg)}</span>
      <span>p80 {round(summary.p80)}</span>
      <span>max {round(summary.max)}</span>
    </div>
  )
}

export const KpiTile: React.FC<{ label: React.ReactNode; value: string; to?: string }> = ({ label, value, to }) => (
  <div className="bg-white rounded-lg shadow p-4 relative">
    {to && (
      <Link to={to} className="absolute top-2 right-2 text-gray-400 hover:text-primary" title="Open metric">
        ↗
      </Link>
    )}
    <div className="text-2xl font-bold text-gray-800">{value}</div>
    <div className="text-sm text-gray-500">{label}</div>
  </div>
)

/** Period selector for one chart "version": Overall / a month / a from–to range. */
export const PeriodPicker: React.FC<{ months: string[]; period: Period; onChange: (p: Period) => void; onRemove?: () => void }> = ({
  months,
  period,
  onChange,
  onRemove
}) => {
  const last = months[months.length - 1] ?? ''
  const first = months[0] ?? last
  const monthOptions = months.map((m) => (
    <option key={m} value={m}>
      {m}
    </option>
  ))
  return (
    <div className="flex items-center gap-2 text-sm flex-wrap">
      <select
        className="select select-bordered select-xs"
        value={period.kind}
        onChange={(e) => {
          const kind = e.target.value
          if (kind === 'all') onChange({ kind: 'all' })
          else if (kind === 'month') onChange({ kind: 'month', month: period.kind === 'month' ? period.month : last })
          else onChange({ kind: 'range', from: first, to: last })
        }}
      >
        <option value="all">Overall</option>
        <option value="month">Month</option>
        <option value="range">Range</option>
      </select>
      {period.kind === 'month' && (
        <select className="select select-bordered select-xs" value={period.month} onChange={(e) => onChange({ kind: 'month', month: e.target.value })}>
          {monthOptions}
        </select>
      )}
      {period.kind === 'range' && (
        <>
          <select className="select select-bordered select-xs" value={period.from} onChange={(e) => onChange({ kind: 'range', from: e.target.value, to: period.to })}>
            {monthOptions}
          </select>
          <span>–</span>
          <select className="select select-bordered select-xs" value={period.to} onChange={(e) => onChange({ kind: 'range', from: period.from, to: e.target.value })}>
            {monthOptions}
          </select>
        </>
      )}
      {onRemove && (
        <button className="btn btn-ghost btn-xs" onClick={onRemove} title="Remove version">
          ✕
        </button>
      )}
    </div>
  )
}

export const Card: React.FC<{ title: string; to?: string; summary?: Summary; children: React.ReactNode }> = ({ title, to, summary, children }) => (
  <div className="bg-white rounded-lg shadow p-4">
    <div className="flex items-center justify-between mb-2">
      <h3 className="font-semibold text-gray-700">{title}</h3>
      {to && (
        <Link to={to} className="text-gray-400 hover:text-primary" title="Open metric">
          ↗
        </Link>
      )}
    </div>
    {summary && <SummaryLine summary={summary} />}
    {children}
  </div>
)
